import { useContext, useEffect, useState } from "react";
import type { FC } from "react";
import { useNavigate } from "react-router-dom";
import AppContext from "../../utils/appContext";
import { useFolderContent } from "../../hooks";
import { clearSession, getStoredUser } from "../../lib/auth";
import { Badge, BlockingSpinner } from "../ui";
import Breadcrumbs from "./Breadcrumbs";
import FileGrid from "./FileGrid";
import FileTable from "./FileTable";
import FileToolbar from "./FileToolbar";
import SelectionToolbar from "./SelectionToolbar";
import { useFileSelection } from "./hooks/useFileSelection";
import { useFolderData } from "./hooks/useFolderData";
import { useInfiniteFiles } from "./hooks/useInfiniteFiles";
import type { FileBrowserProps } from "./types";

const viewList = ["small", "medium", "large"];

const FileBrowserPage: FC<FileBrowserProps> = ({
  folderPath,
  refreshCounter,
  groups,
  toggleActiveTab,
}) => {
  const [view, setView] = useState(viewList[1]);
  const [user] = useState(getStoredUser);
  const context = useContext(AppContext);
  const navigate = useNavigate();

  const logout = () => {
    clearSession();
    context?.updateLoginStatus(false);
    navigate("/login");
  };

  const folderContent = useFolderContent({
    onSessionExpired: logout,
  });

  const folderData = useFolderData({
    folderPath,
    groups,
    view,
    folderContent,
  });

  const showBlockingLoader = folderData.loading || folderData.loadingMoreFiles;

  const selection = useFileSelection({
    files: folderData.files,
    unfilteredFiles: folderData.unfilteredFiles,
    folderContent,
    showBlockingLoader,
    setFiles: folderData.setFiles,
    setUnfilteredFiles: folderData.setUnfilteredFiles,
    setTotalFiles: folderData.setTotalFiles,
    setFolderError: folderData.setFolderError,
    setFolderSuccess: folderData.setFolderSuccess,
    setLoading: folderData.setLoading,
    updatePermissionList: folderData.updatePermissionList,
    getInitialFiles: folderData.getInitialFiles,
    getTotalFiles: folderData.getTotalFiles,
  });

  const roles = JSON.parse(user.groups || "[]");

  const toggleView = () => {
    setView(viewList[(viewList.indexOf(view) + 1) % viewList.length]);
  };

  const sendUpFolderPath = (path: string) => {
    navigate(`/files${path}`);
  };

  const goToFolder = (index: number) => {
    const pathArray = folderData.pathMenu.slice(0, index + 1);
    sendUpFolderPath("/" + pathArray.join("/"));
  };

  useInfiniteFiles({
    filesLength: folderData.files.length,
    loading: folderData.loading,
    loadingMoreFiles: folderData.loadingMoreFiles,
    page: folderData.page,
    totalFiles: folderData.totalFiles,
    loadingMoreFilesRef: folderData.loadingMoreFilesRef,
    setPage: folderData.setPage,
    getFiles: folderData.getFiles,
  });

  useEffect(() => {
    folderData.getFolders().then(() => folderData.getInitialFiles());
    folderData.setFolderSuccess("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, refreshCounter]);

  useEffect(() => {
    folderData.setPage(0);
    folderData.fetchData();
    selection.clearSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folderPath]);

  useEffect(() => {
    folderData.getTotalFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folderPath, refreshCounter]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const parentButton = target.closest("button");
      if (
        !parentButton?.classList.contains("size-button") &&
        !target.closest(".download-menu") &&
        !target.closest(".download-button")
      ) {
        folderData.setActiveDownloadMenu(null);
      }
    };

    const timeoutId = setTimeout(() => {
      document.addEventListener("click", handleClickOutside);
    }, 500);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("click", handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folderData.activeDownloadMenu]);

  useEffect(() => {
    folderPath !== "/" &&
      folderData.showContent &&
      folderData.getFolderInfo(folderPath);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folderData.showContent]);

  useEffect(() => {
    if (window.innerWidth < 1000) {
      setView("list");
    }
  }, []);

  useEffect(() => {
    const preventUnload = (event: BeforeUnloadEvent) => {
      if (!showBlockingLoader) return;

      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", preventUnload);

    return () => {
      window.removeEventListener("beforeunload", preventUnload);
    };
  }, [showBlockingLoader]);

  return (
    <div className="min-h-[300px] lg:pb-20 fade-in">
      {showBlockingLoader && <BlockingSpinner />}

      <div className="grid items-end relative my-5 py-3 md:py-5">
        <h1 className="flex relative flex-wrap md:min-h-[2rem] gap-2 items-center text-xl md:text-2xl">
          {folderData.showContent && (
            <>
              <span className="fade-in">
                {folderData.folderNames[folderData.folderNames?.length - 1]
                  ? folderData.folderNames[folderData.folderNames?.length - 1]
                      ?.folder_name
                  : "Browse our files"}
              </span>
              {folderPath !== "/" && folderData.groupLabels.length > 0 && (
                <span className="inline-flex gap-1 fade-in">
                  {folderData.groupLabels.map((group: any) => (
                    <Badge key={`index-${group.ID}`} color={group.colour}>
                      {group.name}
                    </Badge>
                  ))}
                </span>
              )}
            </>
          )}
        </h1>
      </div>
      <div className="relative items-center justify-between mb-3 md:flex md:mb-0 md:mb-5">
        <FileToolbar
          folderPath={folderPath}
          roles={roles}
          view={view}
          folderError={folderData.folderError}
          folderSuccess={folderData.folderSuccess}
          toggleTab={toggleActiveTab}
          toggleView={toggleView}
          setView={setView}
        />
        <Breadcrumbs
          pathMenu={folderData.pathMenu}
          loading={folderData.loading}
          showBlockingLoader={showBlockingLoader}
          sendUpFolderPath={sendUpFolderPath}
          goToFolder={goToFolder}
        />
      </div>

      <div className="border-b relative overflow-x-scroll border-slate-100">
        {folderData.showContent && (
          <FileGrid
            folders={folderData.folders}
            groups={groups}
            roles={roles}
            view={view}
            refresh={folderData.refresh}
            selectFolder={sendUpFolderPath}
          />
        )}

        {folderData.showContent &&
          (roles.includes(0) || roles.includes(1)) &&
          Array.isArray(folderData.files) &&
          folderData.files.length > 0 && (
            <SelectionToolbar
              allSelected={selection.allSelected}
              availablePermissions={folderData.availablePermissions}
              selectedItems={selection.selectedItems}
              showBlockingLoader={showBlockingLoader}
              selectAll={selection.selectAll}
              filterByGroup={selection.filterByGroup}
              deleteSelected={selection.deleteSelected}
            />
          )}

        {folderData.showContent && (
          <FileTable
            files={folderData.files}
            groups={groups}
            roles={roles}
            view={view}
            loadingMoreFiles={folderData.loadingMoreFiles}
            showBlockingLoader={showBlockingLoader}
            activeDownloadMenu={folderData.activeDownloadMenu}
            setActiveDownloadMenu={folderData.setActiveDownloadMenu}
            selectedItems={selection.selectedItems}
            selectItem={selection.selectItem}
          />
        )}
      </div>
    </div>
  );
};

export default FileBrowserPage;
